import { AuthApiError } from "@onlooker/auth-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { nameForKey } from "../api/labelsApi";
import {
	type BrowserStatus,
	getLesson,
	type Lesson,
	type RetractableOrg,
	retractOrgLesson,
	setLessonStatus,
} from "../api/lessonsApi";
import { ConfirmAction } from "../components/ConfirmAction";
import { Icon } from "../components/Icon";
import { PALETTE } from "../components/palette";
import {
	Button,
	Chip,
	EmptyState,
	Loading,
	Panel,
	STATUS_ICONS,
	StatusBadge,
} from "../components/ui";
import { When } from "../components/When";
import { describeError } from "../lib/apiErrors";
import type { LessonsContext } from "./LessonsPage";

// One lesson, read in full. Rendered out of the list LessonsPage already
// holds, because the list returns full bodies - so clicking down the column
// issues nothing. GET /api/lessons/:id is the fallback for the one case that
// cannot work that way: an id not in any loaded page, which is what a pasted
// link is.

function Field({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div style={{ marginBottom: "1rem" }}>
			{/*
			  h3, not h2: `Field` only ever renders inside a titled `Panel`
			  (Applies to / Why it was trusted), and that title is itself the
			  h2. Nesting this under it as an h3 is correct document order -
			  h1 (the section, from AppShell) -> h2 (the claim, and the panels
			  beside it) -> h3 (the field) - not a skip.
			  The font size is an explicit inline style below, so this is a
			  semantic-only change - nothing here should look different.
			*/}
			<h3
				style={{
					margin: "0 0 0.35rem",
					fontFamily: "var(--font-data)",
					fontSize: "var(--text-data-sm)",
					letterSpacing: "1px",
					textTransform: "uppercase",
					color: PALETTE.muted,
				}}
			>
				{label}
			</h3>
			{children}
		</div>
	);
}

function Chips({ values }: { values: string[] }) {
	return (
		<div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
			{values.map((value) => (
				<Chip key={value}>{value}</Chip>
			))}
		</div>
	);
}

export default function LessonDetail() {
	const { id } = useParams();
	// `retractableIn` renamed on the way in: the local derivation below needs
	// that exact name, for the same reason `own` and `authorName` are not
	// called `ownedIds` or `authors` either - and a shadowed binding here
	// would silently read the context's whole map where the render below
	// means one lesson's answer.
	const {
		lessons,
		ownedIds,
		authors,
		poolSettled,
		patchLesson,
		retractableIn: retractableIn_,
		formerAuthors,
		labels,
	} = useOutletContext<LessonsContext>();

	// The loaded page is the source of truth whenever it holds this id, so a
	// retraction written into it by patchLesson shows here without a refetch.
	const listed = lessons.find((lesson) => lesson.id === id) ?? null;

	const [fetched, setFetched] = useState<Lesson | null>(null);
	/**
	 * Whether the caller owns whatever `fetched` holds.
	 *
	 * Tracked beside `fetched` rather than read from `ownedIds` at render,
	 * because the two sources do not overlap: a deep-linked lesson is on no
	 * loaded page, so `ownedIds` cannot answer for it, and a filter refetch
	 * can empty `ownedIds` while this pane still shows a lesson out of
	 * `fetched`. Set from the same value that set `fetched`, so they cannot
	 * describe different lessons.
	 */
	const [fetchedOwn, setFetchedOwn] = useState(false);
	/**
	 * The author name for whatever `fetched` holds, the same way `fetchedOwn`
	 * answers for ownership - `authors` cannot answer for a deep-linked
	 * lesson that is on no loaded page, which is the one case `fetched` is
	 * for.
	 */
	const [fetchedAuthorName, setFetchedAuthorName] = useState<string | null>(
		null,
	);
	/**
	 * Whether `fetched`'s author's account has since been anonymized, the
	 * same way `fetchedAuthorName` answers for the author name -
	 * `formerAuthors` cannot answer for a deep-linked lesson that is on no
	 * loaded page, which is the one case `fetched` is for.
	 */
	const [fetchedFormerAuthor, setFetchedFormerAuthor] = useState(false);
	/**
	 * The org this viewer may retract `fetched` in, the same way
	 * `fetchedAuthorName` answers for the author name - set from the same
	 * `getLesson` response that sets `fetched`, so the two cannot describe
	 * different lessons.
	 */
	const [fetchedRetractableIn, setFetchedRetractableIn] =
		useState<RetractableOrg | null>(null);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [pending, setPending] = useState(false);
	const [actionError, setActionError] = useState<{
		message: string;
		retryable: boolean;
		attempted: BrowserStatus;
	} | null>(null);
	// Held apart from `pending`/`actionError` above: this is a different
	// request on a different endpoint, and sharing either would disable the
	// wrong control's spinner or let one action's failure paint over the
	// other's.
	const [orgPending, setOrgPending] = useState(false);
	const [orgActionError, setOrgActionError] = useState<{
		message: string;
		retryable: boolean;
	} | null>(null);
	// Bumped whenever a transition settles for the lesson still on screen -
	// success or failure alike. ConfirmAction owns "armed" internally now, so
	// this is what stands in for the old `setConfirming(false)`: paired with
	// `id` into `resetToken` below, a change in either disarms ConfirmAction
	// without remounting it - `resetToken` exists precisely so the instance
	// stays alive and focus lands back on the trigger, rather than being
	// destroyed and rebuilt the way a fresh `key` would. Counts settlements
	// across the whole page's lifetime, not per lesson: it is never reset
	// when `id` changes, so a stale count from a previous lesson can share a
	// value with a fresh one on a new lesson - `id` is the other half of the
	// pairing precisely because this alone cannot disambiguate that case.
	const [settleCount, setSettleCount] = useState(0);

	// The id in scope right now, read from inside `transition`'s async
	// continuation. A plain closure over `id` would read the value from the
	// render the click happened in, which is exactly the value that has gone
	// stale once the user has navigated on - this ref is what lets that
	// continuation notice the difference.
	const currentId = useRef(id);
	currentId.current = id;

	// LessonDetail is reconciled in place when :id changes - same route
	// element, same position - so neither of these resets on its own. A stale
	// error or a stuck "Working..." from the previous id must not bleed into
	// this one. Same defect fetchError had below, in the one flow here where
	// getting it wrong is destructive. ConfirmAction's armed state used to be
	// reset here too; it now disarms itself through `resetToken` below.
	useEffect(() => {
		setActionError(null);
		setPending(false);
		setOrgActionError(null);
		setOrgPending(false);
	}, [id]);

	// The list can vanish underneath this pane: a failed filter refetch sets
	// lessons back to null, and without this the pane would blank a lesson the
	// user is reading and then re-fetch one it already had. The list query
	// failing says nothing about the lesson on screen.
	useEffect(() => {
		if (listed) {
			setFetched(listed);
			setFetchedOwn(ownedIds.includes(listed.id));
			setFetchedAuthorName(authors[listed.id] ?? null);
			setFetchedRetractableIn(retractableIn_[listed.id] ?? null);
			setFetchedFormerAuthor(formerAuthors.includes(listed.id));
		}
	}, [listed, ownedIds, authors, retractableIn_, formerAuthors]);

	useEffect(() => {
		// Nothing to do while the id is in memory, and nothing to decide until
		// the pool has been asked at all - `listed` being null before then means
		// "not checked yet", not "not present". Effects run child-first, so
		// without poolSettled this fires before LessonsPage's own load() has
		// even started, for every id, including ones already in the pool.
		//
		// `fetched?.id === id` also holds: a filter refetch can null out
		// `listed` for the lesson currently open, and without this an already-
		// held copy would be fetched again just because the list no longer
		// carries it.
		if (!id || listed || !poolSettled || fetched?.id === id) return;

		let live = true;
		setFetchError(null);
		getLesson(id)
			.then(({ lesson, own, author_name, retractable_in, former_author }) => {
				if (live) {
					setFetched(lesson);
					setFetchedOwn(own);
					setFetchedAuthorName(author_name);
					setFetchedRetractableIn(retractable_in);
					setFetchedFormerAuthor(former_author);
				}
			})
			.catch((error) => {
				// A fallback distinct from the EmptyState title below, so a
				// non-Error rejection - which describeError has no way to
				// produce today - would not print the same sentence twice.
				if (live)
					setFetchError(describeError(error, "That lesson could not be read."));
			});

		// The list pane stays mounted while the detail changes, so a slow
		// response for a lesson the user has already navigated away from would
		// otherwise overwrite the one they are looking at.
		return () => {
			live = false;
		};
	}, [id, listed, poolSettled]);

	const lesson = listed ?? (fetched?.id === id ? fetched : null);

	/**
	 * Whether this lesson is the viewer's to act on.
	 *
	 * `ownedIds` answers for a listed lesson; `fetchedOwn` answers for one
	 * only this pane holds. Defaults to false, which is the safe direction:
	 * an unknown owner renders no control rather than one that 404s.
	 */
	const own = listed
		? ownedIds.includes(listed.id)
		: fetched?.id === id && fetchedOwn;

	/**
	 * Same split as `own`: `authors` answers for a listed lesson, and
	 * `fetchedAuthorName` answers for one only this pane holds.
	 */
	const authorName = listed
		? (authors[listed.id] ?? null)
		: fetched?.id === id
			? fetchedAuthorName
			: null;

	/**
	 * Whether this lesson's author's account has since been anonymized.
	 *
	 * Same listed/fetched split as `authorName` and for the same reason:
	 * `formerAuthors` cannot answer for a deep-linked lesson that is on no
	 * loaded page, and a filter refetch can clear it while this pane still
	 * shows a lesson out of `fetched`. An anonymized account carries no name
	 * AND cannot authenticate to set a new one (the API's `resolvePrincipal`
	 * refuses it), so this and `authorName` never both hold for the same
	 * lesson - the attribution block below reads `authorName` first and
	 * falls through to this one otherwise, rather than the two racing.
	 */
	const formerAuthor = listed
		? formerAuthors.includes(listed.id)
		: fetched?.id === id && fetchedFormerAuthor;

	/**
	 * The org this viewer may retract this lesson in, or null.
	 *
	 * Same listed/fetched split as `authorName` and for the same reason: the
	 * sidecar cannot answer for a deep-linked lesson that is on no loaded
	 * page, and a filter refetch can empty it while this pane still shows a
	 * lesson out of `fetched`.
	 */
	const retractableIn: RetractableOrg | null = listed
		? (retractableIn_[listed.id] ?? null)
		: fetched?.id === id
			? fetchedRetractableIn
			: null;

	const transition = async (next: BrowserStatus) => {
		if (!id || pending) return;
		// Captured once, up front: `id` from `useParams` can change under this
		// same call while it is in flight, and every write below needs to know
		// which lesson it was actually asked to move, not which one is on
		// screen when the promise settles.
		const target = id;
		setPending(true);
		setActionError(null);
		try {
			await setLessonStatus(target, next);
			// AFTER the round-trip, never before. The server returns { id, seq }
			// rather than the lesson, but it wrote body.status and the status
			// column together in one batch - so writing the status it just
			// accepted is reflecting its answer, not guessing at it. Ungated by
			// `currentId`: the server did write, so the list must carry that
			// regardless of where the user has since navigated.
			patchLesson(target, next);
			setFetched((current) =>
				current && current.id === target
					? { ...current, status: next }
					: current,
			);
		} catch (error) {
			// transitionLesson can exhaust its sequence retries, which apps/api
			// turns into a 503 whose message says nothing was written. That is a
			// guarantee no other failure here makes, and it is the difference
			// between "press it again" and "do not". Read off `code` rather than
			// the message, because describeError only carries the text.
			const retryable =
				error instanceof AuthApiError && error.code === "sequence_contention";
			// Gated: a rejection that settles after the user has moved to
			// another lesson must not paint this one's error under that one.
			if (currentId.current === target) {
				setActionError({
					message: describeError(
						error,
						"Could not change that lesson's status.",
					),
					retryable,
					attempted: next,
				});
			}
		} finally {
			// Gated, and true either way the round-trip went: a live "Yes,
			// retract" surviving a failure sits directly over an error saying
			// the same click would fail again, and a request that settles for
			// a lesson the user has left must not touch the one now showing.
			if (currentId.current === target) {
				setPending(false);
				setSettleCount((count) => count + 1);
			}
		}
	};

	// This lesson is not the viewer's own; it reaches org level through
	// `retractOrgLesson` instead of `setLessonStatus`, which is still
	// `WHERE id = ? AND user_id = ?` and would only 404 here. One-way - there
	// is no org-level route back to active - which is why this has no
	// "Make active" counterpart the way `transition` does.
	const retractForOrg = async (orgId: string) => {
		if (!id || orgPending) return;
		// Captured once, up front, for the same reason `transition` does:
		// `id` from `useParams` can change under this same call while it is
		// in flight.
		const target = id;
		setOrgPending(true);
		setOrgActionError(null);
		try {
			await retractOrgLesson(orgId, target);
			// AFTER the round-trip, never before - matching `transition`.
			// Ungated by `currentId`: the server did write, so the list must
			// carry that regardless of where the user has since navigated.
			patchLesson(target, "retracted");
			setFetched((current) =>
				current && current.id === target
					? { ...current, status: "retracted" }
					: current,
			);
		} catch (error) {
			// Same rule `transition` reads off `code`: a 503 whose code is
			// sequence_contention promises nothing was written, so it is worth
			// retrying; everything else, including a 404, is not.
			const retryable =
				error instanceof AuthApiError && error.code === "sequence_contention";
			// Gated, matching `transition`: a rejection that settles after the
			// user has moved to another lesson must not paint this one's error
			// under that one.
			if (currentId.current === target) {
				setOrgActionError({
					// A 404 here can mean the lesson is no longer shared with
					// this org, or that the caller is no longer its owner -
					// requireOrgRole's "No such org" and retractOrgLesson's
					// "No such lesson" are deliberately indistinguishable at
					// the server (the design's Errors rule says the copy
					// "must not claim to know which"), so rendering either
					// message verbatim would claim to know a cause. "No such
					// org" in particular reads as the org itself being gone,
					// which is wrong for the demoted-owner case that is the
					// common way this actually fires.
					message:
						error instanceof AuthApiError && error.code === "not_found"
							? "This lesson could not be retracted for the org. It may no longer be shared there, or it may no longer be yours to act for."
							: describeError(
									error,
									"Could not retract that lesson for the org.",
								),
					retryable,
				});
			}
		} finally {
			// Gated, matching `transition`, and sharing its `settleCount`: both
			// are "a transition settled for the lesson still on screen," and
			// the author and org controls never render at once, so one counter
			// disarming either ConfirmAction is unambiguous.
			if (currentId.current === target) {
				setOrgPending(false);
				setSettleCount((count) => count + 1);
			}
		}
	};

	const back = (
		// Narrow shows one pane at a time, so this is the only way back to the
		// list. lessons.css hides it above the breakpoint, where the list is
		// already beside it.
		<Link
			className="lessons-back"
			to="/lessons"
			style={{ color: PALETTE.accent }}
		>
			← All lessons
		</Link>
	);

	// `!lesson` and not just `fetchError`: LessonDetail is not remounted when
	// :id changes - same route element, same position - so an error from a
	// previous, absent id survives in state. Once `listed` finds the new id in
	// memory there is something to show instead, and the stale error should
	// not win the render just because nothing has cleared it yet.
	if (fetchError && !lesson) {
		return (
			<>
				{back}
				<EmptyState title="Could not load that lesson">{fetchError}</EmptyState>
			</>
		);
	}

	if (!lesson) {
		return (
			<>
				{back}
				<Loading label="Loading that lesson…" />
			</>
		);
	}

	const { applies_to: appliesTo, consensus, evidence } = lesson;
	// Root labels only on the viewer's own lesson: a root key hashes a path,
	// and someone else's path hashing to it says nothing about the repository.
	const projectName = nameForKey(labels, evidence.project_key, {
		rootLabels: own,
	});

	// A retracted lesson can be made active again; an active one can be
	// retracted. Those are the only two a human may assert - and apps/api
	// enforces that with a 400 regardless of what this renders. `null` for the
	// other two statuses, which get no control at all.
	//
	// Gated on ownership too. The pool shows other people's public lessons,
	// but transitionLesson is still `WHERE id = ? AND user_id = ?`, so this
	// control on somebody else's lesson can only 404 - and the same reasoning
	// already written over the retry button below applies: a button that
	// reliably fails is worse than no button. apps/api is the enforcement;
	// this only avoids offering what it will refuse.
	const next: BrowserStatus | null = !own
		? null
		: lesson.status === "active"
			? "retracted"
			: lesson.status === "retracted"
				? "active"
				: null;
	const verb = next === "retracted" ? "Retract" : "Make active";

	return (
		<>
			{back}
			{/*
			  Judgment above the rule, facts below. Status, then the claim -
			  the one thing here that must be read carefully, so it stays
			  unboxed and leads - then the rationale that justifies it. A rule
			  separates that judgment from the two panels answering the two
			  questions the remaining six facts actually split into. Retract
			  sits last, beneath both: it reaches every mirror on its next
			  delta pull, so the evidence is passed on the way to the button,
			  not after it.
			*/}
			<div
				style={{
					display: "flex",
					gap: "var(--space-4)",
					alignItems: "center",
					flexWrap: "wrap",
					marginBottom: "var(--space-3)",
				}}
			>
				<span
					style={{
						display: "flex",
						alignItems: "center",
						gap: "var(--space-1)",
					}}
				>
					<Icon name={STATUS_ICONS[lesson.status]} />
					<StatusBadge status={lesson.status} />
				</span>
				<span
					style={{
						display: "flex",
						alignItems: "center",
						gap: "var(--space-1)",
					}}
				>
					<Icon name="Trophy" />
					<time dateTime={lesson.promoted_at} style={{ color: PALETTE.muted }}>
						Promoted {new Date(lesson.promoted_at).toLocaleDateString()}
					</time>
				</span>
			</div>

			{/*
			  The claim leads, because it is the thing being trusted or not -
			  and it stays in the readable face. It is a sentence, not chrome,
			  and pixel type is measurably harder to read at length: it leads
			  by size and weight here, not by face.
			*/}
			<h2
				style={{
					margin: "0 0 var(--space-3)",
					fontFamily: "var(--font-body)",
					fontSize: "var(--text-body-lg)",
				}}
			>
				{lesson.claim}
			</h2>

			<p style={{ margin: "0 0 var(--space-4)" }}>{lesson.rationale}</p>

			<hr
				style={{
					border: "none",
					borderTop: `2px solid ${PALETTE.border}`,
					margin: "0 0 var(--space-4)",
				}}
			/>

			<div className="lessons-detail-panels">
				<Panel title="Applies to" icon="MagnifyingGlass">
					<Field label="Stack">
						<Chips values={appliesTo.stack} />
					</Field>

					<Field label="Scope">
						{appliesTo.scope.kind === "versioned" ? (
							<Chips
								values={Object.entries(appliesTo.scope.versions).map(
									([name, range]) => `${name} ${range}`,
								)}
							/>
						) : (
							// The justification is the point of this branch: a lesson
							// with no version constraint never expires, so the reason
							// is judged rather than assumed.
							<p style={{ margin: 0 }}>{appliesTo.scope.justification}</p>
						)}
					</Field>

					{appliesTo.file_patterns.length > 0 ? (
						<Field label="Files">
							<Chips values={appliesTo.file_patterns} />
						</Field>
					) : null}

					{appliesTo.task_kinds.length > 0 ? (
						<Field label="Tasks">
							<Chips values={appliesTo.task_kinds} />
						</Field>
					) : null}
				</Panel>

				<Panel title="Why it was trusted" icon="Trophy">
					<Field label="Consensus">
						<p style={{ margin: 0 }}>
							{consensus.agreed} of {consensus.judges} judges agreed on{" "}
							<When iso={consensus.decided_at} />
						</p>
					</Field>

					<Field label="What was observed">
						<p style={{ margin: 0 }}>{evidence.resolution}</p>
						<p style={{ margin: "0.35rem 0 0", color: PALETTE.muted }}>
							<When iso={evidence.observed_at} />
							{evidence.session_ids.length > 0 && (
								<>
									{" · "}
									{evidence.session_ids.length} session
									{evidence.session_ids.length === 1 ? "" : "s"}
								</>
							)}
							{" · "}
							{evidence.artifact_ids.length} artifact
							{evidence.artifact_ids.length === 1 ? "" : "s"}
						</p>
					</Field>

					{authorName ? (
						<Field label="Author">
							<p style={{ margin: 0 }}>{authorName}</p>
						</Field>
					) : formerAuthor ? (
						// The server says only that the author's account has been
						// anonymized - never a name, since a literal here could not
						// be told apart from a member who set that as their actual
						// name. "Former member" is this client's wording alone.
						<Field label="Author">
							<p style={{ margin: 0 }}>Former member</p>
						</Field>
					) : null}

					<Field label="Provenance">
						{/*
						  project_key and author_key are opaque by design - the
						  mapping to a repository lives only in a local manifest,
						  and author_key carries the unlinkability guarantee. They
						  are shown because they are what a person correlates two
						  lessons by, not because they mean anything on their own -
						  which is why the key stays beside the viewer's name for
						  it rather than giving way to it.
						*/}
						<Chips
							values={[
								// Named only when it is someone else's. Hiding the
								// status control without saying why reads as a
								// missing feature rather than a boundary, and this
								// is the one fact that explains it.
								...(own ? [] : ["from: another account"]),
								`source: ${lesson.source}`,
								`visibility: ${lesson.visibility}`,
								projectName
									? `project: ${projectName} (${evidence.project_key})`
									: `project: ${evidence.project_key}`,
							]}
						/>
					</Field>
				</Panel>
			</div>

			{next ? (
				<div>
					<ConfirmAction
						trigger={verb}
						question={
							next === "retracted"
								? "Stop trusting this lesson everywhere?"
								: "Trust this lesson again everywhere?"
						}
						confirmLabel={`Yes, ${verb.toLowerCase()}`}
						variant={next === "retracted" ? "danger" : "primary"}
						pending={pending}
						// LessonDetail is reconciled in place when :id changes - same
						// route element, same position - so nothing here resets on its
						// own. An armed confirm prompt following the user onto a
						// lesson they never opened one on puts a live "Yes, retract"
						// over the wrong claim, and a retraction reaches every mirror
						// on its next delta pull. `id` covers navigating to a new
						// lesson; `settleCount` covers a round trip settling - success
						// or failure alike - for the one still on screen, which needs
						// the same disarm and used to live here as
						// `setConfirming(false)` in `transition`'s finally.
						resetToken={`${id}:${settleCount}`}
						onConfirm={() => void transition(next)}
					/>

					{actionError ? (
						<div role="alert" style={{ marginTop: "0.75rem" }}>
							<p style={{ color: PALETTE.danger, margin: "0 0 0.5rem" }}>
								{actionError.message}
							</p>
							{/*
							  Offered only where the server promised nothing was
							  written. A 400 would fail identically on a second
							  press, and a button that reliably fails is worse
							  than no button.
							*/}
							{actionError.retryable ? (
								<Button
									loading={pending}
									loadingLabel="Working..."
									// The status this same failure was raised
									// against, not the current `next` - if the
									// pool refetches between the failure and this
									// click and the lesson's status has since
									// moved, `next` would have flipped too and
									// this would retry the opposite transition.
									onClick={() => void transition(actionError.attempted)}
								>
									Try again
								</Button>
							) : null}
						</div>
					) : null}
				</div>
			) : null}

			{/*
			  The other case `next` does not cover: not the viewer's own, but
			  an org they own may still retract it, through the authority
			  Task 1-5 resolved server-side - this client re-derives none of
			  it. `!own` is asserted here too even though the server is not
			  supposed to populate `retractableIn` for the viewer's own
			  lesson (D6) - belt and suspenders against ever rendering both
			  controls on the same lesson, since the author's pair is
			  strictly more capable.

			  Gated on status too, but NOT the way an earlier version of this
			  comment argued - "must not be more permissive than the
			  author's own pair" - which is wrong and has been removed. The
			  author's own pair offers nothing for `refuted` or `superseded`
			  because its `next` is a two-state toggle with nothing left to
			  toggle there, not because retraction is inappropriate for
			  those statuses: `BROWSER_TRANSITIONS` accepts `retracted` from
			  any status, and the org route does too. Withholding this
			  control for a `refuted` org lesson too would leave an owner's
			  only route for it "ask the platform operator" - the exact
			  outcome D6 exists to prevent.

			  So the gate is `!== "retracted"`: withheld only once the
			  lesson is already retracted, because there is no org-level
			  route back to active, so once used the control simply
			  disappears rather than reading "Make active". Without this
			  gate the control survives its own success: `retractOrgLesson`
			  carries no server-side precondition, so a second click on an
			  already-retracted lesson would "succeed" again, appending
			  another feed row while appearing to have done nothing new.
			*/}
			{!own && retractableIn && lesson.status !== "retracted" ? (
				<div>
					<ConfirmAction
						trigger={`Retract for ${retractableIn.org_name}`}
						question={`This lesson is not yours. Retracting it stops ${retractableIn.org_name} from reading it, and only its author can make it active again — you cannot undo this.`}
						confirmLabel="Yes, retract for the org"
						variant="danger"
						pending={orgPending}
						resetToken={`${id}:${settleCount}`}
						onConfirm={() => void retractForOrg(retractableIn.org_id)}
					/>

					{orgActionError ? (
						<div role="alert" style={{ marginTop: "0.75rem" }}>
							<p style={{ color: PALETTE.danger, margin: "0 0 0.5rem" }}>
								{orgActionError.message}
							</p>
							{/*
							  Same reasoning as the author control's own retry:
							  offered only where the server promised nothing was
							  written.
							*/}
							{orgActionError.retryable ? (
								<Button
									loading={orgPending}
									loadingLabel="Working..."
									onClick={() => void retractForOrg(retractableIn.org_id)}
								>
									Try again
								</Button>
							) : null}
						</div>
					) : null}
				</div>
			) : null}
		</>
	);
}
